import { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ArrowLeft, User, FileText, GraduationCap, CheckCircle2, Clock, Plus, RefreshCw } from "lucide-react";
import api from "@/services/api";
import {
    getCachedAgencyStudentDetail,
    setCachedAgencyStudentDetail,
    type StudentDetail,
} from "@/services/agencyStudentDetailCache";

function StageBadge({ stage }: { stage: string }) {
    return (
        <Badge variant="outline" className="text-[9px] font-black px-2 py-0.5 rounded-full uppercase tracking-widest border border-blue-100 text-blue-600 bg-blue-50/50 whitespace-nowrap">
            {stage || "—"}
        </Badge>
    );
}

function formatFetchedAt(ms: number): string {
    const d = new Date(ms);
    return d.toLocaleString("en-GB", {
        day: "2-digit",
        month: "short",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
    });
}

export default function AgencyStudentDetailView() {
    const navigate = useNavigate();
    const { memberId } = useParams<{ memberId: string }>();
    const [data, setData] = useState<StudentDetail | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(false);
    const [lastFetchedAt, setLastFetchedAt] = useState<number | null>(null);
    const [isRefreshing, setIsRefreshing] = useState(false);

    useEffect(() => {
        if (!memberId) return;
        let cancelled = false;

        // Serve from cache instantly if fetched within the last 30 seconds.
        // This avoids a redundant API call when the user navigates back from
        // the application list or another sub-page.
        const cached = getCachedAgencyStudentDetail(memberId);
        if (cached) {
            setData(cached.detail);
            setLastFetchedAt(cached.fetchedAt);
            setLoading(false);
            return () => { cancelled = true; };
        }

        const fetchData = async () => {
            try {
                const res = await api.get(`/agency/students/${memberId}/`);
                if (cancelled) return;
                const initialData: StudentDetail = res.data;
                setData(initialData);
                setCachedAgencyStudentDetail(memberId, initialData);
                setLastFetchedAt(Date.now());

                // Bulk SF sync — keeps loading true until fresh data is available,
                // matching the portal applications pattern. Non-fatal: DB values
                // already in state if this call fails.
                try {
                    const bulkRes = await api.get(`/agency/students/${memberId}/applications/bulk-detail/`);
                    if (cancelled || bulkRes.data.status !== 'success') return;

                    const freshApps: Array<{ application_number: string; stage: string; status: string }> =
                        bulkRes.data.applications ?? [];

                    const freshByNumber = new Map(
                        freshApps.map((fa) => [fa.application_number, fa] as const)
                    );

                    setData((prev) => {
                        if (!prev) return prev;
                        const merged: StudentDetail = {
                            ...prev,
                            applications: prev.applications.map((app) => {
                                const fresh = freshByNumber.get(app.application_number);
                                if (!fresh) return app;
                                return {
                                    ...app,
                                    stage: fresh.stage || app.stage,
                                    status: fresh.status || app.status,
                                };
                            }),
                        };
                        setCachedAgencyStudentDetail(memberId, merged);
                        return merged;
                    });
                } catch {
                    // Non-fatal — DB values already rendered.
                }
            } catch {
                if (!cancelled) setError(true);
            } finally {
                if (!cancelled) setLoading(false);
            }
        };

        fetchData();

        return () => { cancelled = true; };
    }, [memberId]);

    const handleRefresh = async () => {
        if (!memberId || isRefreshing) return;
        setIsRefreshing(true);
        try {
            const res = await api.get(`/agency/students/${memberId}/`);
            const refreshedData: StudentDetail = res.data;
            setData(refreshedData);
            setCachedAgencyStudentDetail(memberId, refreshedData);
            setLastFetchedAt(Date.now());
            setError(false);

            // Sync application stages from Salesforce on manual refresh.
            try {
                const bulkRes = await api.get(`/agency/students/${memberId}/applications/bulk-detail/`);
                if (bulkRes.data.status !== 'success') return;

                const freshApps: Array<{ application_number: string; stage: string; status: string }> =
                    bulkRes.data.applications ?? [];

                const freshByNumber = new Map(
                    freshApps.map((fa) => [fa.application_number, fa] as const)
                );

                setData((prev) => {
                    if (!prev) return prev;
                    const merged: StudentDetail = {
                        ...prev,
                        applications: prev.applications.map((app) => {
                            const fresh = freshByNumber.get(app.application_number);
                            if (!fresh) return app;
                            return {
                                ...app,
                                stage: fresh.stage || app.stage,
                                status: fresh.status || app.status,
                            };
                        }),
                    };
                    setCachedAgencyStudentDetail(memberId, merged);
                    return merged;
                });
            } catch {
                // Non-fatal — refreshed DB values already shown.
            }
        } catch {
            // Ignore refresh errors; keep existing data visible.
        } finally {
            setIsRefreshing(false);
        }
    };

    if (loading) {
        return (
            <div className="space-y-4">
                <Card className="h-40 animate-pulse rounded-2xl border border-slate-100" />
                <Card className="h-40 animate-pulse rounded-2xl border border-slate-100" />
            </div>
        );
    }

    if (error || !data) {
        return (
            <div className="flex flex-col items-center gap-4 py-16 text-center">
                <p className="text-sm font-bold text-slate-700">Student not found.</p>
                <Button variant="outline" size="sm" onClick={() => navigate("/partner/students")} className="rounded-xl h-9 font-bold">
                    <ArrowLeft className="h-4 w-4 mr-2" /> Back to Students
                </Button>
            </div>
        );
    }

    const { member, documents, applications } = data;

    return (
        <div className="space-y-6">
            <div className="flex items-center justify-between gap-3">
                <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => navigate("/partner/students")}
                    className="text-slate-500 hover:text-primary font-bold rounded-xl h-9 px-3"
                >
                    <ArrowLeft className="h-4 w-4 mr-1" /> Back
                </Button>
                <Button
                    variant="outline"
                    size="sm"
                    onClick={handleRefresh}
                    disabled={isRefreshing}
                    className="flex items-center gap-2 font-black text-xs border-slate-200 text-slate-600 hover:text-primary rounded-xl px-4 h-9"
                >
                    <RefreshCw className={`w-3.5 h-3.5 ${isRefreshing ? "animate-spin" : ""}`} />
                    {isRefreshing ? "Refreshing…" : "Refresh"}
                </Button>
            </div>

            <Card className="border border-slate-100 shadow-sm rounded-2xl bg-white p-6">
                <div className="flex items-center gap-3 mb-5">
                    <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center">
                        <User className="h-5 w-5 text-primary" />
                    </div>
                    <div>
                        <p className="text-xs text-slate-400 uppercase tracking-widest font-bold">Student</p>
                        <h2 className="text-xl font-serif font-black text-slate-900">
                            {member.first_name} {member.last_name}
                        </h2>
                    </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-10 gap-y-4">
                    <div>
                        <p className="text-[10px] text-slate-400 uppercase tracking-widest font-black mb-0.5">First Name</p>
                        <p className="text-sm text-slate-800 font-medium">{member.first_name}</p>
                    </div>
                    <div>
                        <p className="text-[10px] text-slate-400 uppercase tracking-widest font-black mb-0.5">Citizenship</p>
                        <p className="text-sm text-slate-800 font-medium">{member.citizenship || "—"}</p>
                    </div>
                    <div>
                        <p className="text-[10px] text-slate-400 uppercase tracking-widest font-black mb-0.5">Last Name</p>
                        <p className="text-sm text-slate-800 font-medium">{member.last_name}</p>
                    </div>
                    <div>
                        <p className="text-[10px] text-slate-400 uppercase tracking-widest font-black mb-0.5">Email</p>
                        <p className="text-sm text-slate-800 font-medium">{member.email || "—"}</p>
                    </div>
                    <div>
                        <p className="text-[10px] text-slate-400 uppercase tracking-widest font-black mb-0.5">Gender</p>
                        <p className="text-sm text-slate-800 font-medium">{member.gender || "—"}</p>
                    </div>
                    <div>
                        <p className="text-[10px] text-slate-400 uppercase tracking-widest font-black mb-0.5">Mobile</p>
                        <p className="text-sm text-slate-800 font-medium">{member.phone || "—"}</p>
                    </div>
                </div>
            </Card>

            <Card className="border border-slate-100 shadow-sm rounded-2xl bg-white overflow-hidden">
                <div className="flex items-center gap-3 px-6 py-4 border-b border-slate-100">
                    <div className="w-8 h-8 rounded-lg bg-slate-100 flex items-center justify-center">
                        <FileText className="h-4 w-4 text-slate-500" />
                    </div>
                    <h3 className="text-base font-serif font-black text-slate-900">Documents</h3>
                    <span className="text-xs text-slate-400 ml-auto">{documents.length} item{documents.length !== 1 ? "s" : ""}</span>
                </div>

                {documents.length === 0 ? (
                    <div className="px-6 py-8 text-center text-sm text-slate-400">No documents found.</div>
                ) : (
                    <table className="w-full text-left border-collapse">
                        <thead>
                            <tr className="bg-slate-50/50 border-b border-slate-100">
                                <th className="px-6 py-3 text-[10px] font-black text-slate-400 uppercase tracking-widest">Document Name</th>
                                <th className="px-6 py-3 text-[10px] font-black text-slate-400 uppercase tracking-widest">Required</th>
                                <th className="px-6 py-3 text-[10px] font-black text-slate-400 uppercase tracking-widest">Status</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-50">
                            {documents.map((doc) => (
                                <tr key={doc.id}>
                                    <td className="px-6 py-3 text-sm text-slate-700 font-medium capitalize">
                                        {doc.document_type.replace(/_/g, " ")}
                                    </td>
                                    <td className="px-6 py-3">
                                        {doc.required ? (
                                            <CheckCircle2 className="h-4 w-4 text-emerald-500" />
                                        ) : (
                                            <Clock className="h-4 w-4 text-slate-300" />
                                        )}
                                    </td>
                                    <td className="px-6 py-3 text-sm text-slate-700">
                                        {doc.status}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
            </Card>

            <Card className="border border-slate-100 shadow-sm rounded-2xl bg-white overflow-hidden">
                <div className="flex items-center gap-3 px-6 py-4 border-b border-slate-100">
                    <div className="w-8 h-8 rounded-lg bg-slate-100 flex items-center justify-center">
                        <GraduationCap className="h-4 w-4 text-slate-500" />
                    </div>
                    <h3 className="text-base font-serif font-black text-slate-900">Applications</h3>
                    <span className="text-xs text-slate-400">{applications.length} item{applications.length !== 1 ? "s" : ""}</span>
                    <div className="ml-auto">
                        <Button
                            size="sm"
                            onClick={() => navigate(`/partner/students/${memberId}/create-application`)}
                            className="h-8 px-3 rounded-lg text-xs font-bold bg-[#001B44] hover:bg-[#002a6e] text-white gap-1.5"
                        >
                            <Plus className="h-3.5 w-3.5" />
                            Create New Application
                        </Button>
                    </div>
                </div>

                {applications.length === 0 ? (
                    <div className="px-6 py-8 text-center text-sm text-slate-400">No applications found.</div>
                ) : (
                    <table className="w-full text-left border-collapse">
                        <thead>
                            <tr className="bg-slate-50/50 border-b border-slate-100">
                                <th className="px-6 py-3 text-[10px] font-black text-slate-400 uppercase tracking-widest">Application Number</th>
                                <th className="px-6 py-3 text-[10px] font-black text-slate-400 uppercase tracking-widest hidden md:table-cell">Applied Program Name</th>
                                <th className="px-6 py-3 text-[10px] font-black text-slate-400 uppercase tracking-widest hidden lg:table-cell">University Name</th>
                                <th className="px-6 py-3 text-[10px] font-black text-slate-400 uppercase tracking-widest">Stage</th>
                                <th className="px-6 py-3 text-[10px] font-black text-slate-400 uppercase tracking-widest text-right">Action</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-50">
                            {applications.map((app) => (
                                <tr key={app.application_number} className="group hover:bg-slate-50/30 transition-colors">
                                    <td className="px-6 py-3">
                                        <span className="text-xs font-bold text-slate-600 bg-slate-100 px-2 py-0.5 rounded-md">
                                            {app.application_number}
                                        </span>
                                    </td>
                                    <td className="px-6 py-3 hidden md:table-cell">
                                        <span className="text-sm text-slate-700">{app.applied_program_name || "—"}</span>
                                    </td>
                                    <td className="px-6 py-3 hidden lg:table-cell">
                                        <span className="text-sm text-slate-500">{app.institution_name || "—"}</span>
                                    </td>
                                    <td className="px-6 py-3">
                                        <StageBadge stage={app.stage} />
                                    </td>
                                    <td className="px-6 py-3 text-right">
                                        <Button
                                            variant="outline"
                                            size="sm"
                                            onClick={() => navigate(`/partner/applications/${app.application_number}`)}
                                            className="h-8 font-bold text-xs rounded-lg px-3 border-slate-200 text-slate-600 hover:text-primary"
                                        >
                                            Go to Application
                                        </Button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
            </Card>

            <div className="flex justify-end px-2 pb-2">
                <p className="text-[10px] text-slate-400">
                    {isRefreshing
                        ? "Refreshing…"
                        : lastFetchedAt
                          ? `Last fetched: ${formatFetchedAt(lastFetchedAt)}`
                          : ""}
                </p>
            </div>
        </div>
    );
}
